<template>
  <div
    id="app"
    :class="[$router.currentRoute.name]"
    v-chakra="
      (theme) => ({
        position: 'relative',
        zIndex: 0,
        fontWeight: 'light',
      })
    "
  >
    <TopNav
      v-if="user"
      :sticky="true"
      :nav-items="topNavItems"
      :title="title"
      title-href="/"
      title-type="routerLink"
      @navigate="onNavigate"
    />
    <c-flex :class="mainRowClass" height="100%" :flex-wrap="{ base: 'wrap', md: 'initial' }">
      <SideBar v-if="user" :nav-items="sidebarNavItems" @navigate="onNavigate" />

      <Wrapper v-if="!isEmbeddedContent">
        <div class="container-fluid">
          <div class="row">
            <div class="col-12"><router-view class="view" /></div>
          </div>
        </div>
      </Wrapper>
      <IframeWrapper v-else>
        <router-view class="view" />
      </IframeWrapper>
    </c-flex>
    <FeatureGuard feature="gdprDialog">
      <dialog-modal
        v-if="gdprDialogActive && user"
        :show-dialog="gdprDialogActive"
        @close="onSaveGdprDialogHandled"
        content-ref="gdpr-dialog-content"
        data-cy="gdpr-dialog"
        :show-close-button="false"
        :close-on-overlay-click="false"
        size="5xl"
      >
        <template v-slot:title>
          <c-heading as="h2" size="md" ref="gdprTitle" tabindex="-1" :_focus="{ outlineWidth: 0 }">
            {{ $t("common.gdpr.title") }}
          </c-heading>
        </template>
        <template v-slot:content>
          <c-heading as="h2" size="sm" mb="sm">{{ $t("common.gdpr.subtitle") }}</c-heading>
          <div
            v-html="$t('common.gdpr.text')"
            v-chakra="{
              columnCount: { base: 1, md: 2 },
              columnGap: '4rem',
              orphans: 2,
              widow: 2,
              p: {
                mb: 'md',
                fontSize: 'sm',
              },
              h2: {
                mb: 'sm',
                fontSize: 'md',
                fontWeight: 'bold',
                breakAfter: 'avoid',
              },
            }"
          />
        </template>
        <template v-slot:actions>
          <c-flex :flex-direction="{ base: 'column', md: 'row' }">
            <c-button
              variation="solid"
              @click="onSaveGdprDialogHandled"
              background="brand.beige"
              border-color="brand.beige"
              white-space="normal"
              :mr="{ md: 'sm' }"
              :mb="{ base: 'sm', md: '0' }"
              left-icon="check"
              data-cy="gdpr-dialog-consent-button"
            >
              {{ $t("common.gdpr.consent") }}
            </c-button>
            <c-button
              variation="solid"
              @click="dissent"
              white-space="normal"
              left-icon="cross"
              data-cy="gdpr-dialog-dissent-button"
            >
              {{ $t("common.gdpr.dissent") }}
            </c-button>
          </c-flex>
        </template>
      </dialog-modal>
    </FeatureGuard>

    <!-- General error dialog -->
    <dialog-modal
      v-if="generalError.active"
      :show-dialog="generalError.active"
      @close="onGeneralErrorDialogClose"
      content-ref="general-error-dialog-content"
    >
      <template v-slot:title>
        <c-text v-if="generalError.isBlockedError" as="span" color="brand.red">{{
          $t("dialog.forbiddenOperation.title")
        }}</c-text>
        <c-text v-else as="span" color="brand.red">{{ generalError.dialogTitle }}</c-text>
      </template>
      <template v-slot:content>
        <template v-if="generalError.isBlockedError">
          <c-text>{{ $t("common.blockedInfo") }}</c-text>
          <ContactPerson></ContactPerson>
        </template>
        <template v-else>
          <c-text>{{ generalError.dialogText || "" }}</c-text>
          <c-text v-if="generalError.error" mt="md">({{ generalError.error.message }})</c-text>
        </template>
      </template>
      <template v-slot:actions>
        <c-button
          variation="solid"
          @click="onGeneralErrorDialogClose"
          background="brand.beige"
          borderColor="brand.beige"
          data-cy="close-general-error-dialog"
        >
          {{ $t("common.closeButton") }}
        </c-button>
      </template>
    </dialog-modal>

    <!-- General info dialog -->
    <dialog-modal
      v-if="generalInfo.active"
      :show-dialog="generalInfo.active"
      @close="onGeneralInfoDialogClose"
      content-ref="general-info-dialog-content"
      data-cy="general-info-dialog"
    >
      <template v-slot:title>
        {{ generalInfo.dialogTitle }}
      </template>
      <template v-slot:content>
        <p>{{ generalInfo.dialogText || "" }}</p>
      </template>
      <template v-slot:actions>
        <c-button
          variation="solid"
          @click="onGeneralInfoDialogClose"
          background="brand.beige"
          borderColor="brand.beige"
          data-cy="general-info-dialog-close-button"
        >
          {{ $t("common.okButton") }}
        </c-button>
      </template>
    </dialog-modal>
  </div>
</template>

<script lang="ts">
import Vue, { defineComponent } from "vue";
import ContactPerson from "@/components/ContactPerson.vue";
import FeatureGuard from "@/components/FeatureGuard.vue";
import Modal from "@/components/Modal.vue";
import config from "@/config";
import Button from "@/design-system/elements/Button.vue";
import IframeWrapper from "@/design-system/elements/IframeWrapper.vue";
import Wrapper from "@/design-system/elements/Wrapper.vue";
import SideBar from "@/design-system/patterns/SideBar.vue";
import TopNav from "@/design-system/patterns/TopNav.vue";
import { INavItem, ISideBarNavItem, ITopNavItem } from "@/NavInterfaces";
import { routes } from "@/router/routes";
import { CButton, CFlex, CHeading, CText } from "@chakra-ui/vue";
import {
  CorePermission,
  ICategory,
  IOrderRow,
  Logger,
  SubsidiaryIds,
  IRootState,
  IUser,
} from "@iloq/shared";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { get } from "lodash";
import { mapState } from "vuex";
import { routerPush } from "@/router/router-push-wrapper";
import { IMaterialState } from "./store/materialModule";

const appInsightConnectionString = config.get("appInsightConnectionString") as string;

try {
  if (appInsightConnectionString) {
    const appInsights = new ApplicationInsights({
      config: {
        connectionString: appInsightConnectionString,
      },
    });
    appInsights.loadAppInsights();
    appInsights.trackPageView(); // Manually call trackPageView to establish the current user/session/pageview
  }
} catch (error) {
  Logger.error(error);
}

const itemCount = (items: IOrderRow[], serviceproductIds?: string[]): number | undefined => {
  return (
    (serviceproductIds &&
      items?.filter((item) => !serviceproductIds.includes(item.product.id))?.length) ||
    undefined
  );
};

const getTopNavItems = function getTopNavItems(
  $t: (key: string) => string,
  cartItems: IOrderRow[],
  serviceproductIds?: string[],
): ITopNavItem[] {
  return [
    {
      name: $t("topnav.shoppingCart.title"),
      icon: "cart",
      href: "/shopping_cart",
      label: "",
      type: "routerLink",
      count: itemCount(cartItems, serviceproductIds),
    },
  ];
};

const getSidebarNavItems = function getSidebarNavItems(
  $t: (key: string) => string,
  hideReturns: boolean,
  subscriptionViewAllowed: boolean,
): ISideBarNavItem[] {
  const result: ISideBarNavItem[] = [
    {
      id: "1",
      name: $t("sidebar.home.title"),
      href: "/",
      active: false,
      type: "routerLink",
      icon: "home",
    },
    {
      id: "2-products-for-restricted-user-group",
      name: $t("sidebar.ordering.products.title"),
      href: "/products",
      active: false,
      type: "routerLink",
      icon: "cart",
    },
    {
      id: "2-ordering-for-other-user-groups",
      name: $t("sidebar.ordering.products.title"),
      icon: "cart",
      children: [
        {
          id: "21",
          name: $t("sidebar.ordering.products.title"),
          href: "/products",
          active: false,
          type: "routerLink",
        },
        {
          id: "22",
          name: $t("sidebar.ordering.orders.title"),
          href: "/orders",
          active: false,
          type: "routerLink",
        },
        ...(!subscriptionViewAllowed
          ? [
              {
                id: "23",
                name: $t("sidebar.ordering.licenses.title"),
                href: "/ordering/licenses",
                active: false,
                type: "routerLink",
              } as INavItem,
            ]
          : []),
        {
          id: "24",
          name: $t("sidebar.ordering.shoppingCart.title"),
          href: "/shopping_cart",
          active: false,
          type: "routerLink",
        },
      ],
    },
    ...(subscriptionViewAllowed
      ? [
          {
            id: "3",
            name: $t("sidebar.ordering.subscriptions.title"),
            icon: "licenses",
            children: [
              {
                id: "31",
                name: $t("sidebar.ordering.subscriptions.new"),
                href: "/subscriptions/new",
                active: false,
                type: "routerLink",
              },
              {
                id: "32",
                name: $t("sidebar.ordering.subscriptions.fiveseriesplus.title"),
                href: "/subscriptions/list",
                active: false,
                type: "routerLink",
              },
              {
                id: "34",
                name: $t("sidebar.ordering.subscriptions.fiveseries.title"),
                href: "/ordering/licenses",
                active: false,
                type: "routerLink",
              },
            ],
          } as INavItem,
        ]
      : []),
    {
      id: "4",
      name: $t("sidebar.learning.title"),
      icon: "calendar_learning",
      children: [
        {
          id: "41",
          name: $t("sidebar.learning.academy.title"),
          href: "/learning",
          active: false,
          type: "routerLink",
        },
        {
          id: "42",
          name: $t("sidebar.learning.events.title"),
          href: "/events",
          active: false,
          type: "routerLink",
        },
      ],
    },
    {
      id: "5",
      name: $t("sidebar.support.title"),
      icon: "support",
      children: [
        {
          id: "51",
          name: $t("sidebar.support.instructions.title"),
          href: "/support/instructions",
          active: false,
          type: "routerLink",
        },
        {
          id: "52",
          name: $t("sidebar.support.solutions.title"),
          href: "/support/solutions",
          active: false,
          type: "routerLink",
        },
        hideReturns
          ? {
              id: "53",
              name: $t("sidebar.support.returns.title"),
              href: "/support/returns/contact",
              active: false,
              type: "routerLink",
            }
          : {
              id: "53",
              name: $t("sidebar.support.returns.title"),
              href: "/support/returns",
              active: false,
              type: "routerLink",
            },
        {
          id: "supportRequests",
          name: $t("sidebar.support.tickets.title"),
          href: "/support/requests",
          active: false,
          type: "routerLink",
        },
      ],
    },
    {
      id: "6",
      name: $t("sidebar.marketingMaterial.title"),
      icon: "marketing",
      children: [],
    },
    {
      id: "7",
      name: $t("sidebar.brandstore.title"),
      href: "/brandstore",
      active: false,
      type: "routerLink",
      icon: "brandstore",
    },
    {
      id: "8",
      name: $t("sidebar.bulletin.title"),
      href: "/bulletin",
      active: false,
      type: "routerLink",
      icon: "info",
    },
    {
      id: "partnerProgram",
      name: "Partner Program",
      href: "/partner_program",
      active: false,
      type: "routerLink",
      icon: "partner_program",
    },
  ];

  return result;
};

const checkPermission = (item: INavItem, user?: IUser): boolean => {
  if (!item.href) {
    return true;
  }
  if (!user) {
    return false;
  }
  const route = routes.find(
    (it: any) => it.path === item.href && it.meta && it.meta.permissionsRequired,
  );
  if (route) {
    const routePermissions = route.meta?.permissionsRequired;
    return !!routePermissions?.every((permission: CorePermission) =>
      user.permissions.includes(permission),
    );
  }
  return true;
};

const filterNavItemsByPermissions = (items: INavItem[], user: IUser): INavItem[] =>
  items.filter((item) => checkPermission(item, user));

const filterSidebarNavItemsByPermissions = (
  items: ISideBarNavItem[],
  user: IUser,
  idsToDisplayAlways: string[] = [],
): ISideBarNavItem[] => {
  const filteredItems: ISideBarNavItem[] = filterNavItemsByPermissions(items, user);
  filteredItems.forEach((item) => {
    if (item.children) {
      item.children = item.children.filter((child: any) => checkPermission(child, user));
    }
  });
  // Don't show main items that have had all their children removed, unless they have a href or
  // are "forced" visible
  return filteredItems.filter(
    (item) =>
      item.href ||
      (item.children && item.children.length > 0) ||
      (item.id && idsToDisplayAlways.indexOf(item.id) >= 0),
  );
};

export default defineComponent({
  name: "App",
  components: {
    Button,
    Wrapper,
    SideBar,
    TopNav,
    IframeWrapper,
    "dialog-modal": Modal,
    FeatureGuard,
    CButton,
    CText,
    CHeading,
    CFlex,
    ContactPerson,
  },
  data() {
    // Inline SVG so that fill can be changed dynamically
    return {
      title: `<svg viewBox="0 0 218 43" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M9.77795 14.0949H2.4751V39.1512H9.77795V14.0949Z" fill="currentColor"/>
<path d="M10.2707 6.15542C10.2707 3.85668 8.40539 2 6.13535 2C3.86531 2 2 3.85668 2 6.15542C2 8.43648 3.84771 10.3108 6.13535 10.3108C8.42299 10.3108 10.2707 8.43648 10.2707 6.15542Z" fill="currentColor"/>
<path d="M65.4908 22.6887C65.4908 28.3825 60.8628 32.9977 55.1613 32.9977C49.4598 32.9977 44.8317 28.3825 44.8317 22.6887C44.8317 16.9949 49.4598 12.3797 55.1613 12.3797C60.8628 12.3797 65.4908 16.9949 65.4908 22.6887ZM72.4945 22.6887C72.4945 13.1224 64.7342 5.37738 55.1437 5.37738C45.5708 5.37738 37.7928 13.1224 37.7928 22.6887C37.7928 32.255 45.5532 40 55.1437 40C64.7342 40 72.4945 32.255 72.4945 22.6887Z" fill="currentColor"/>
<path d="M36.9832 32.2727H24.7004V6.15543H17.2216V39.1689H33.0591L36.9832 32.2727Z" fill="currentColor"/>
<path d="M106.985 33.1215C109.185 30.2215 110.487 26.6142 110.487 22.6887C110.487 17.2248 107.953 12.362 103.994 9.19684L100.228 15.2089C102.216 17.0833 103.466 19.7357 103.466 22.6887C103.466 28.3825 98.8375 32.9977 93.136 32.9977C91.8514 32.9977 90.6372 32.7501 89.4934 32.3257L85.7276 38.3201C87.98 39.3811 90.4789 39.9823 93.1184 39.9823H93.136L93.1184 40H112.722L116.417 34.094L116.963 33.1038H106.985V33.1215Z" fill="currentColor"/>
<path d="M100.703 7.09261C98.4152 5.9786 95.846 5.34203 93.136 5.34203C83.5631 5.34203 75.7852 13.087 75.7852 22.6533C75.7852 28.188 78.3895 33.1038 82.4369 36.269L86.1851 30.2746C84.1086 28.3825 82.8065 25.6771 82.8065 22.6533C82.8065 16.9595 87.4345 12.3444 93.136 12.3444C94.491 12.3444 95.7756 12.6096 96.9546 13.087L100.703 7.09261Z" fill="currentColor"/>
<path d="M131 5V19.6593H132.661V13.0751H135.547C138.137 13.0751 140.055 11.6878 140.055 9.01683C140.055 6.38725 138.236 5 135.251 5H131ZM132.661 11.46V6.61501H135.547C137.248 6.61501 138.276 7.52605 138.276 9.03753C138.276 10.549 137.228 11.46 135.547 11.46H132.661Z" fill="currentColor"/>
<path d="M145.799 5L139.631 19.6593H141.509L142.952 16.0566H149.951L151.375 19.6593H153.312L147.361 5H145.799ZM146.551 7.23617L149.319 14.4416H143.605L146.511 7.23617H146.551Z" fill="currentColor"/>
<path d="M155.173 5V19.6593H156.833V13.0751H158.969L162.765 19.6593H164.801L160.827 12.9094C163.121 12.516 164.327 11.0874 164.327 9.01683C164.327 6.44937 162.943 5 158.988 5H155.173ZM156.833 11.46V6.61501H159.265C161.499 6.61501 162.547 7.38111 162.547 9.03753C162.547 10.4455 161.835 11.46 158.811 11.46H156.833Z" fill="currentColor"/>
<path d="M165.389 5V6.61501H170.016V19.6593H171.676V6.61501H176.303V5H165.389Z" fill="currentColor"/>
<path d="M178.264 5V19.6593H179.925V7.48463H179.965L188.07 19.6593H190.166V5H188.505V17.1747H188.466L180.36 5H178.264Z" fill="currentColor"/>
<path d="M193.652 5V19.6593H202.806V18.0443H195.313V12.8266H201.976V11.2116H195.313V6.61501H202.45V5H193.652Z" fill="currentColor"/>
<path d="M205.372 5V19.6593H207.032V13.0751H209.168L212.964 19.6593H215L211.026 12.9094C213.319 12.516 214.526 11.0874 214.526 9.01683C214.526 6.44937 213.142 5 209.187 5H205.372ZM207.032 11.46V6.61501H209.464C211.698 6.61501 212.746 7.38111 212.746 9.03753C212.746 10.4455 212.034 11.46 209.009 11.46H207.032Z" fill="currentColor"/>
<path d="M131 24.968V39.6273H132.661V33.043H135.547C138.137 33.043 140.055 31.6558 140.055 28.9848C140.055 26.3552 138.236 24.968 135.251 24.968H131ZM132.661 31.428V26.583H135.547C137.248 26.583 138.276 27.494 138.276 29.0055C138.276 30.517 137.228 31.428 135.547 31.428H132.661Z" fill="currentColor"/>
<path d="M149.224 24.5953C144.954 24.5953 141.909 27.8874 141.909 32.2976C141.909 36.7079 144.973 40 149.224 40C153.475 40 156.539 36.6872 156.539 32.2976C156.539 27.9288 153.514 24.5953 149.224 24.5953ZM143.688 32.2976C143.688 28.8813 146.021 26.2103 149.204 26.2103C152.447 26.2103 154.76 28.8813 154.76 32.2976C154.76 35.7347 152.427 38.385 149.224 38.385C145.962 38.385 143.688 35.6933 143.688 32.2976Z" fill="currentColor"/>
<path d="M159.208 24.968V39.6273H160.869V33.043H163.004L166.8 39.6273H168.836L164.862 32.8774C167.156 32.484 168.362 31.0553 168.362 28.9848C168.362 26.4173 166.978 24.968 163.024 24.968H159.208ZM160.869 31.428V26.583H163.3C165.535 26.583 166.582 27.3491 166.582 29.0055C166.582 30.4135 165.871 31.428 162.846 31.428H160.869Z" fill="currentColor"/>
<path d="M169.424 24.968V26.583H174.051V39.6273H175.712V26.583H180.338V24.968H169.424Z" fill="currentColor"/>
<path d="M185.032 24.968L178.863 39.6273H180.742L182.185 36.0246H189.184L190.607 39.6273H192.545L186.594 24.968H185.032ZM185.783 27.2041L188.551 34.4096H182.837L185.744 27.2041H185.783Z" fill="currentColor"/>
<path d="M194.405 24.968V39.6273H202.472V38.0123H196.066V24.968H194.405Z" fill="currentColor"/>
</svg>`,
    };
  },
  computed: {
    ...mapState({
      user: (state: unknown) => (state as IRootState).user,
      generalError: (state: unknown) => (state as IRootState).generalError,
      generalInfo: (state: unknown) => (state as IRootState).generalInfo,
    }),
    ...mapState("material", {
      rootCategory: (state: unknown) => (state as IMaterialState).rootCategory,
    }),
    ...mapState({
      gdprDialogActive: (state: any) => state.gdprDialog.active,
    }),
    cartItems(): IOrderRow[] {
      return this.$store.state.shoppingCart.cartRows;
    },
    topNavItems() {
      const user = (this as any).user as IUser; // Some acrobatics needed, possibly fixed in Vue 3
      const fullName = user ? `${user.firstName} ${user.lastName}` : "NOT LOGGED IN";
      const { ids: serviceproductIds, products: serviceproducts } =
        this.$store.state.productData?.serviceproducts ?? {};

      const items = filterNavItemsByPermissions(
        getTopNavItems(
          (key) => (this as any).$t(key),
          (this as any).cartItems,
          serviceproducts && serviceproductIds,
        ),
        user,
      );
      const profileItem = items.find((it) => it.href === "/profile");
      if (profileItem) {
        profileItem.label = fullName;
      }
      items.forEach((item) => {
        item.active = item.href === this.$route.path;
      });

      return items;
    },

    sidebarNavItems() {
      const user = (this as any).user as IUser; // Some acrobatics needed, possibly fixed in Vue 3
      const hideReturns =
        user.subsidiaryId === SubsidiaryIds.USA && config.get("disableReturnsForInc") === true;

      const subscriptionViewAllowed =
        !user.groups.map((group) => group.toLocaleLowerCase()).includes("country_other") &&
        config.get("subscriptionViewAllowedCountryList").includes(user.countryCode);

      const unfilteredItems = getSidebarNavItems(
        (key) => (this as any).$t(key),
        hideReturns,
        subscriptionViewAllowed,
      );
      const materialNavItemID = "6";
      let items = filterSidebarNavItemsByPermissions(unfilteredItems, user, [materialNavItemID]);

      const filterDisabledFeaturesFromNavigation = (item: ISideBarNavItem) => {
        const featureEnabled = get(this.$store.state, ["features", String(item!.id)]);
        // If you're wondering why we specifically check for false here (instead of just checking whether featureEnabled is falsy), it's because our feature flag implementation assumes any feature that is not specifically disabled (set to false) is enabled.
        if (featureEnabled === false) return false;
        return true;
      };
      items = items
        .filter((item) => {
          item.children = item.children?.filter((childItem) =>
            filterDisabledFeaturesFromNavigation(childItem),
          );
          return filterDisabledFeaturesFromNavigation(item);
        })
        .filter((item) => {
          // Filter out ordering for restricted user group
          if (item.id === "2-ordering-for-other-user-groups") {
            if (
              !(
                user.permissions.includes(CorePermission.order) ||
                user.permissions.includes(CorePermission.licenses)
              )
            ) {
              return false;
            }
          }
          // Filter out products (first level navi item) for other groups
          if (item.id === "2-products-for-restricted-user-group") {
            if (
              user.permissions.includes(CorePermission.order) ||
              user.permissions.includes(CorePermission.licenses)
            ) {
              return false;
            }
          }
          return true;
        });

      const orderingNavItemIdPrefix = "2-";
      const priceListCategoryId = "43";
      const materialItem = items.find((it) => it.id === materialNavItemID);
      const orderingItem = items.find((it) => it.id!.indexOf(orderingNavItemIdPrefix) === 0);
      const rootCategory = (this as any).rootCategory as ICategory;
      if (materialItem && rootCategory) {
        const addCategoriesAsItems = (category: ICategory) => {
          const translatedName = (this as any).$t(`material.category.${category.id}`);
          const parent =
            category.id.toString() === priceListCategoryId && orderingItem
              ? orderingItem
              : materialItem;

          if (parent.children) {
            parent.children.push({
              id: `5-${category.id}`,
              name: `${translatedName}`,
              href: `/marketing_material?categoryId=${category.id}`,
              active: false,
              type: "routerLink",
            });
          }
        };
        (rootCategory.subcategories || []).forEach(addCategoriesAsItems);
      }

      items.forEach((item) => {
        if (item.href) {
          item.active =
            item.href === this.$route.path ||
            (item.href.length > 1 && this.$route.path.startsWith(item.href));
        } else {
          item.active = false;
        }
        if (item.children) {
          item.children.forEach((child: any) => {
            if (child.href) {
              child.active =
                child.href === this.$route.path || this.$route.path.startsWith(child.href);
              const categoryIdParam = this.$route.query.categoryId;
              if (!child.active && categoryIdParam) {
                child.active = child.href === `${this.$route.path}?categoryId=${categoryIdParam}`;
              }
            } else {
              child.active = false;
            }
          });
        }
      });

      return items;
    },
    isEmbeddedContent() {
      return (
        (this as any).$route.path.indexOf("/ordering/licenses") >= 0 ||
        (this as any).$route.path.indexOf("/subscriptions/") >= 0
      );
    },
    mainRowClass() {
      return (this as any).$route.path.indexOf("/ordering/licenses") >= 0 ? "full" : "";
    },
  },
  methods: {
    onNavigate(path: string) {
      Logger.debug("Navigation to", path);
      if (
        (path.indexOf("/ordering") === 0 && (this.$router as any).history.current.path === path) ||
        (path.indexOf("/subscriptions") === 0 &&
          (this.$router as any).history.current.path === path)
      ) {
        // Shopping cart iframe allows user to navigate away, so clicking cart in main navigation
        // should re-route the iframe to shopping cart (by re-rendering the iframe). And that feels
        // good practice for other iframes also.
        this.$store.commit("iframes/clearIframeUrls");
        Vue.nextTick(() => this.$store.commit("iframes/setIframeUrlsFromConfig"));
      } else {
        routerPush(this, path);
      }
    },
    onGeneralErrorDialogClose() {
      this.$store.commit("hideGeneralError");
    },
    onGeneralInfoDialogClose() {
      this.$store.commit("hideGeneralInfo");
    },
    onSaveGdprDialogHandled() {
      this.$store.dispatch("gdprConsent", true);
    },
    dissent() {
      this.$router.push({ path: "/logout" });
    },
  },
});
</script>

<style lang="scss">
body,
html {
  height: 100%;
  margin: 0;
  padding: 0;
}

#__chakra-app {
  max-width: 1920px;
  margin: 0 auto;
}
#app {
  height: 100%;
  max-width: 1920px;
  margin: 0 auto;
}

.main-row.full {
  height: 100%;
}

.main-row {
  height: 100%;
}

.general-error-dialog,
.gdpr-dialog {
  .v-dialog-content {
    border-radius: 5px !important;
  }
  .v-dialog-header {
    background-color: $color-iloq-red;
    h3 {
      color: white;
      font-size: 16px !important;
      font-weight: 600;
      text-align: center;
    }
    i {
      color: white;
    }
  }
  .v-dialog-body {
    padding: 15px 30px 0 !important;
    overflow: hidden !important;
    text-align: center;
    h1,
    h2,
    h3,
    h4,
    h5 {
      font-weight: 600;
      margin: 14px 0;
    }
    p {
      font-size: 16px;
      color: $color-iloq-gray-dark;
      font-weight: 300;
      margin: 14px 0;
    }
  }
  .dialog-actions {
    text-align: center;
    .button {
      width: 20%;
      margin-left: 10px;
      margin-right: 10px;
    }
    padding: 20px;
  }
}
.vue-phone-number-input {
  input {
    font-family: $font-text !important;
    color: $text-color !important;
    font-size: 16px !important;
    border-radius: 3px;
    border: 1px solid #ddd !important;
    padding-bottom: 1px !important;
    caret-color: $color-iloq-blue-dark !important;
    &:focus {
      border: 1px solid #0a485f !important;
      box-shadow: none !important;
    }
    &:disabled {
      background-color: #f3f3f3;
    }
    &:active {
      box-shadow: none !important;
    }
  }
}

.gdpr-dialog {
  .v-dialog {
    cursor: not-allowed;
    z-index: 99999 !important;
    .button {
      max-height: none;
    }
    .v-dialog-dialog {
      cursor: default;
    }
    .v-dialog-header {
      background-color: $color-iloq-blue-dark !important;
      h3 {
        text-align: left;
      }
    }
    .v-dialog-body {
      overflow: auto !important;
      text-align: left;
    }
    .dialog-actions {
      .button {
        width: 40%;
        white-space: normal;
      }
    }
  }
  .v-dialog--maximize {
    .v-dialog-body {
      height: 90% !important;
    }
    .dialog-actions {
      .button {
        width: 20%;
      }
    }
  }
  .v-dialog-overlay {
    z-index: 99998 !important;
    opacity: 0.7;
  }
}

.form-control:focus {
  border: 1px solid #0a485f;
  box-shadow: none;
}

.screen-reader-only:not(:focus):not(:active) {
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}
</style>
